import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api, { withAcl } from 'hooks/Api';
import useModal from 'ui/partials/Modal/useModal';
import { toastSuccess } from 'ui/partials/Toast';
import { useAdminParams } from 'utils/helpers';
import { useProvider } from 'utils/providers/Provider';
import { Comment } from 'utils/types';
import { useParams } from 'react-router';

import c from '../utils/constants';

interface Payload {
  id: string;
  noteId?: string;
  referenceId?: string;
  parentId?: string;
}

const useDeleteNote = () => {
  const queryClient = useQueryClient();
  const { hideModal } = useModal();
  const { state } = useProvider();
  const { accountId, accountUserId } = useAdminParams();
  const args = state;
  const params = useParams();

  return useMutation({
    mutationFn: async (payload: Payload) => {
      const api = new Api(args);

      const data = await api.delete(
        await withAcl({
          account: {
            url: `api/v1/comments/${payload.id}`,
            params: payload,
          },
          admin: {
            url: `api/v2/accounts/${accountId}/users/${accountUserId}/comments/${payload.id}`,
            params: payload,
          },
        }),
      );

      return data;
    },
    onSuccess: (_, { id, noteId, referenceId }) => {
      if (noteId === id) {
        queryClient.setQueryData([c.NOTES, referenceId], (oldData: any) =>
          oldData.filter((note: Comment) => note._id !== id),
        );

        queryClient.removeQueries({
          queryKey: [c.NOTES, noteId, c.NOTE_COMMENT],
          exact: true,
        });

        toastSuccess('Successfully deleted note');
      } else {
        if (!noteId) {
          queryClient.setQueryData([c.NOTES, referenceId], (oldData: any) =>
            oldData.filter((note: Comment) => note._id !== id),
          );
        } else {
          queryClient.setQueryData([c.NOTES, referenceId], (oldData: any) => {
            const newNotes = [...oldData];

            const noteIndex = newNotes.findIndex((note) => note._id === noteId);

            if (noteIndex > -1) newNotes[noteIndex].comments.total -= 1;

            return newNotes;
          });

          queryClient.invalidateQueries({
            queryKey: [c.NOTES, noteId, c.NOTE_COMMENT],
          });
        }

        toastSuccess('Successfully deleted comment');
      }

      queryClient.invalidateQueries({
        queryKey: [c.LOGS, params.id],
      });

      hideModal();
    },
  });
};

export default useDeleteNote;
